import {
  memo,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  Button,
  cn,
  EmptyState,
  Flex,
  Grid,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

import type {
  Commonv1Value,
  V1CodeManifest,
  V1Step,
} from '@/generated/hooks/grimoire/models';
import type { IStepRunIO } from '@/modules/automation/hooks/useFocusedTestRunStatus';
import type { TStepRunStatus } from '@/modules/automation/utils/step-run-status';
import {
  groupStepsByPhase,
  type IStepPhaseGroup,
} from '@/modules/automation/utils/group-steps-by-phase';
import type { TResolvedValue } from '@/modules/outputs/types';
import {
  AutomationDocumentTitleBar,
  type TAutomationRunningState,
} from '@/modules/automation/components/AutomationDocumentTitleBar';
import type { TAutomationStage } from '@/shared/components/AutomationStageBadge';
import { useRunStepPlayback } from '@/modules/automation/hooks/use-run-step-playback';

import { DraftStepDescription, DraftStepNode } from './DraftStepSummary';
import { ManifestClampedText } from './ManifestClampedText';
import { ManifestIOTable } from './ManifestIOTable';
import { STEP_BODY_ANIM_MS } from './ManifestStepCard';

interface IDraftDocumentStepsViewProps {
  manifest: V1CodeManifest;
  titleFallback?: string;
  automationId?: string;
  /** Resets manual step expansion when the focused test run changes. */
  runId?: string;
  updatedAt?: string;
  /** Render the automation name inside the document. Callers that still mount
   * `AutomationPageHeader` must leave this off so the name appears once. */
  showTitle?: boolean;
  /** Draws the document's own card. Off where a surrounding card owns the frame. */
  framed?: boolean;
  /**
   * Space above the first section, for when nothing sits there (no title bar).
   * Defaults to on when unframed.
   */
  padTop?: boolean;
  /** Whether the in-document name can be renamed. Defaults to `false` so a
   * read-only or version-history viewer can never fire the rename mutation. */
  isTitleEditable?: boolean;
  /**
   * Lifecycle stage for the title bar's badge. Defaults to `draft`; an unbuilt
   * blueprint showing a focused test run's own manifest passes `blueprint`, so
   * the badge doesn't call it a draft.
   */
  stage?: TAutomationStage;
  /** Current draft version. Named in the version-history tooltip only. */
  version?: string;
  /** Opens version history from the title bar's right cluster. */
  onOpenVersionHistory?: () => void;
  /**
   * Set while a run is in flight; replaces the last-saved line in the title
   * bar with the live run state.
   */
  running?: TAutomationRunningState;
  /**
   * Replaces the stage's meta line in the title bar with a state the caller
   * owns. An unbuilt blueprint showing a focused run's manifest sends its
   * build status through here.
   */
  status?: ReactNode;
  /**
   * Extra controls for the title bar's right cluster. Same case as `status`:
   * a blueprint keeps its Approve & build within reach while a focused run's
   * manifest is on screen.
   */
  actions?: ReactNode;
  /** Export the automation, offered in the title bar's options menu. */
  onExport?: () => void;
  /** The built manifest, which adds "Export as Markdown" to the options menu. */
  exportManifest?: V1CodeManifest;
  onOpenWorkbench?: () => void;
  /**
   * Switches the document out for the AOP map. Omitted hides the affordance.
   * No companion `isMapView`: this view only renders over the document.
   */
  onToggleMapView?: () => void;
  selectedStepNumber?: number;
  onSelectStep: (step: V1Step) => void;
  onOpenStep: (step: V1Step) => void;
  onToggleStepInChat?: (step: V1Step) => void;
  isStepReferencedInChat?: (stepNumber: number) => boolean;
  getStepStatus?: (stepNumber: number) => TStepRunStatus | undefined;
  /** The focused test run has started and is not over yet. */
  isRunActive?: boolean;
  getStepIO?: (stepNumber: number) => IStepRunIO | undefined;
  getInputValue?: (name: string) => TResolvedValue | undefined;
  getOutputValue?: (name: string) => TResolvedValue | undefined;
  inputValues?: Record<string, Commonv1Value>;
  onInputValuesChange?: (values: Record<string, Commonv1Value>) => void;
  defaultInputKeys?: string[];
  onToggleDefault?: (name: string) => void;
  inputsReadOnly?: boolean;
  stepDetailsUnavailable?: boolean;
  stepDetailsFailed?: boolean;
}

const OVERVIEW_TEXT_PROPS = { level: 'small', color: 'muted' } as const;

/** A spine segment: thin and neutral at rest, wider in an accent colour. */
function spineSegmentClassName(accent?: string) {
  return cn(
    'shrink-0 transition-[width] duration-300 ease-in-out',
    accent ? ['w-0.5', accent] : 'w-px bg-border/55',
  );
}

/* Room for the hover actions, which float over the row's top-right corner. */
const TITLE_CLEARANCE_TWO_ACTIONS = 'pe-20';
const TITLE_CLEARANCE_ONE_ACTION = 'pe-12';
/* The duration sits beside the actions, so it adds its own room. */
const TITLE_CLEARANCE_DURATION = 'pe-32';

function groupKey(group: IStepPhaseGroup) {
  return `${group.phase}:${group.steps[0]?.number ?? group.steps[0]?.title ?? ''}`;
}

const DraftDocumentStep = memo(function DraftDocumentStep({
  step,
  selected,
  muted,
  isFirst,
  isLast,
  inHoveredGroup,
  isGroupStart,
  followsHoveredGroup,
  onSelect,
  onOpen,
  onToggleStepInChat,
  isReferencedInChat,
  status,
  io,
  isFollowingRun,
  isFollowed,
  isCurrent,
}: {
  step: V1Step;
  selected: boolean;
  muted: boolean;
  isFirst: boolean;
  isLast: boolean;
  /** The hovered step belongs to this step's phase group. */
  inHoveredGroup: boolean;
  /** First step of its phase group — where a group highlight fades in. */
  isGroupStart: boolean;
  /** Sits below the hovered group, so its stub fades the highlight out. */
  followsHoveredGroup: boolean;
  onSelect: (step: V1Step) => void;
  onOpen: (step: V1Step) => void;
  onToggleStepInChat?: (step: V1Step) => void;
  isReferencedInChat: boolean;
  status?: TStepRunStatus;
  io?: IStepRunIO;
  /** A test run is focused, so steps open and close as the run reaches them. */
  isFollowingRun: boolean;
  /** Opened by the run: the step it is on, or the first step before it starts. */
  isFollowed: boolean;
  /** The step the run is on, shown in the selected state without selecting it. */
  isCurrent: boolean;
}) {
  const { t: tAutomation } = useTranslation('automation');
  const contentId = useId();
  const prefersReducedMotion = useReducedMotion();
  const [userExpanded, setUserExpanded] = useState<boolean>();
  const [wasFollowed, setWasFollowed] = useState(isFollowed);
  // A step the user closed still opens when the run reaches it.
  if (isFollowed !== wasFollowed) {
    setWasFollowed(isFollowed);
    if (isFollowed && userExpanded === false) setUserExpanded(undefined);
  }
  const isExpanded = isFollowingRun
    ? (userExpanded ?? isFollowed)
    : status !== 'done' || !!userExpanded;
  const isCollapsible = isFollowingRun || status === 'done';
  const showSelected = selected || isCurrent;
  const durationLabel =
    status === 'done' || status === 'failed' ? io?.durationLabel : undefined;
  const quoteLabel = tAutomation(
    isReferencedInChat
      ? 'automation.manifestSopView.step.removeFromChat'
      : 'automation.manifestSopView.step.addToChat',
  );

  // A run's progress colours the spine ahead of any hover or selection.
  const statusSpine =
    status === 'done'
      ? 'bg-success'
      : status === 'paused'
        ? 'bg-warning'
        : undefined;
  const groupFadeSpine = isGroupStart
    ? 'draft-spine-brand-fade-in'
    : 'draft-spine-brand';

  const topSegmentClassName = cn(
    'h-3',
    spineSegmentClassName(
      statusSpine ??
        (inHoveredGroup
          ? groupFadeSpine
          : followsHoveredGroup
            ? 'draft-spine-brand-fade-out'
            : undefined),
    ),
  );

  const bottomSegmentClassName = cn(
    'min-h-3 flex-1',
    spineSegmentClassName(
      statusSpine ?? (inHoveredGroup ? 'draft-spine-brand' : undefined),
    ),
  );

  /* The row is clickable, but selecting step text must not also select the
   * row — a drag-select ends in a click on the row. */
  const handleRowClick = () => {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) return;
    if (isCollapsible) setUserExpanded(!isExpanded);
    onSelect(step);
  };

  return (
    <Grid
      className={cn(
        'draft-step-row group/step relative -mx-4 w-[calc(100%+2rem)] cursor-pointer grid-cols-[24px_1fr] gap-x-4 rounded-xl px-4 transition-[background-color,opacity]',
        !showSelected && 'draft-hover-fill',
        showSelected && 'draft-selection-fill',
        muted && 'opacity-50',
      )}
      data-testid={`draft-document-step-${step.number}`}
      onClick={handleRowClick}
    >
      {/* Behind the content (`-z-10`) so step text stays selectable; it exists
       * to give the row a keyboard-reachable, labelled control. */}
      <Button
        type="button"
        variant="ghost"
        onClick={event => {
          event.stopPropagation();
          handleRowClick();
        }}
        className="absolute inset-0 -z-10 h-full w-full rounded-xl p-0 !bg-transparent hover:!bg-transparent"
        aria-label={
          step.title || tAutomation('automation.manifestSopView.step.untitled')
        }
        aria-pressed={selected}
        aria-expanded={isCollapsible ? isExpanded : undefined}
        aria-controls={isCollapsible && isExpanded ? contentId : undefined}
        data-testid={`draft-document-step-select-${step.number}`}
      />

      <Flex
        direction="column"
        align="center"
        className="pointer-events-none relative z-10 w-6 shrink-0"
      >
        <span
          data-testid={`draft-document-step-spine-top-${step.number}`}
          className={isFirst ? 'h-3 shrink-0' : topSegmentClassName}
          aria-hidden
        />
        <DraftStepNode
          number={step.number}
          status={status}
          selected={showSelected}
          inHoveredGroup={inHoveredGroup}
        />
        <span
          data-testid={`draft-document-step-spine-bottom-${step.number}`}
          className={
            isLast ? 'min-h-3 flex-1 shrink-0' : bottomSegmentClassName
          }
          aria-hidden
        />
      </Flex>

      <Flex
        direction="column"
        gap={0.5}
        className="relative z-10 min-w-0 select-text py-3 pb-3.5 pe-4"
      >
        <Text
          level="small"
          weight="medium"
          className={cn(
            'leading-6',
            durationLabel
              ? TITLE_CLEARANCE_DURATION
              : onToggleStepInChat
                ? TITLE_CLEARANCE_TWO_ACTIONS
                : TITLE_CLEARANCE_ONE_ACTION,
          )}
        >
          {step.title ||
            tAutomation('automation.manifestSopView.step.untitled')}
        </Text>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              id={contentId}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                duration: prefersReducedMotion ? 0 : STEP_BODY_ANIM_MS / 1000,
              }}
              className="overflow-hidden"
            >
              <DraftStepDescription step={step} selected={showSelected} />
            </motion.div>
          )}
        </AnimatePresence>
      </Flex>

      <Flex
        align="center"
        className="pointer-events-auto absolute end-2.5 top-2.5 z-20 h-8"
      >
        {durationLabel && (
          <Text
            level="xSmall"
            color="muted"
            className="whitespace-nowrap tabular-nums"
          >
            {durationLabel}
          </Text>
        )}
        {/* Collapses to zero width while hidden, so the duration slides over to make room. */}
        <Grid
          className={cn(
            'grid-cols-[0fr] opacity-0 transition-[grid-template-columns,opacity] group-hover/step:grid-cols-[1fr] group-hover/step:opacity-100 group-focus-within/step:grid-cols-[1fr] group-focus-within/step:opacity-100',
            showSelected && 'grid-cols-[1fr] opacity-100',
          )}
        >
          <Flex gap={1} className="min-w-0 overflow-hidden ps-2">
            {onToggleStepInChat && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon-sm"
                    className={cn(
                      'bg-background shadow-xs',
                      isReferencedInChat && 'text-primary',
                    )}
                    aria-label={quoteLabel}
                    aria-pressed={isReferencedInChat}
                    onClick={event => {
                      event.stopPropagation();
                      onToggleStepInChat(step);
                    }}
                  >
                    <Icon type="Quote" size="sm" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{quoteLabel}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  className="bg-background shadow-xs"
                  aria-label={tAutomation(
                    'automation.manifestSopView.step.openPanel',
                  )}
                  onClick={event => {
                    event.stopPropagation();
                    onOpen(step);
                  }}
                >
                  <Icon type="ArrowUpRight" size="sm" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {tAutomation('automation.manifestSopView.step.openPanel')}
              </TooltipContent>
            </Tooltip>
          </Flex>
        </Grid>
      </Flex>
    </Grid>
  );
});

/** Per-step wiring the list threads down to each group. */
interface IDraftStepListProps {
  /** Scopes each row's key, so switching runs remounts rather than reuses. */
  runId?: string;
  selectedStepNumber?: number;
  onSelectStep: (step: V1Step) => void;
  onOpenStep: (step: V1Step) => void;
  onToggleStepInChat?: (step: V1Step) => void;
  isStepReferencedInChat?: (stepNumber: number) => boolean;
  getStepStatus?: (stepNumber: number) => TStepRunStatus | undefined;
  getStepIO?: (stepNumber: number) => IStepRunIO | undefined;
  /** A test run is focused, so steps follow it. */
  isFollowingRun: boolean;
  /** The step the run has open: the one it is on, or the first before it starts. */
  openStepNumber?: number;
  /** The step the run is on. */
  currentStepNumber?: number;
}

/** One phase group's rows. Memoised because a hover anywhere in the list
 * re-renders `DraftStepsList`, and only the group under the pointer and the
 * one below it change appearance. */
const DraftDocumentPhaseGroup = memo(function DraftDocumentPhaseGroup({
  group,
  groupId,
  isHovered,
  followsHoveredGroup,
  isFirstGroup,
  isLastGroup,
  onHoverChange,
  runId,
  selectedStepNumber,
  onSelectStep,
  onOpenStep,
  onToggleStepInChat,
  isStepReferencedInChat,
  getStepStatus,
  getStepIO,
  isFollowingRun,
  openStepNumber,
  currentStepNumber,
}: IDraftStepListProps & {
  group: IStepPhaseGroup;
  groupId: string;
  /** The pointer is over this group and it is one that lifts. */
  isHovered: boolean;
  /** The group above this one is hovered, so its first stub fades the
   * highlight out. */
  followsHoveredGroup: boolean;
  isFirstGroup: boolean;
  isLastGroup: boolean;
  onHoverChange: (groupId: string | undefined) => void;
}) {
  return (
    <Flex
      direction="column"
      /* lattice Flex leaves column children at `items-start`,
       * which would size each row to its own text. */
      align="stretch"
      width="full"
      onMouseEnter={() => onHoverChange(groupId)}
      onMouseLeave={() => onHoverChange(undefined)}
    >
      {group.steps.map((step, stepIndex) => {
        const selected = selectedStepNumber === step.number;
        const isGroupStart = stepIndex === 0;
        const isCurrent =
          selectedStepNumber === undefined &&
          step.number !== undefined &&
          step.number === currentStepNumber;
        return (
          <DraftDocumentStep
            key={`${runId ?? 'authoring'}-${
              step.number ?? `${group.phase ?? 'steps'}-${step.title}`
            }`}
            step={step}
            selected={selected}
            muted={
              (selectedStepNumber ?? currentStepNumber) !== undefined &&
              !selected &&
              !isCurrent
            }
            isCurrent={isCurrent}
            isFollowingRun={isFollowingRun}
            isFollowed={
              step.number !== undefined && step.number === openStepNumber
            }
            isFirst={isFirstGroup && isGroupStart}
            isLast={isLastGroup && stepIndex === group.steps.length - 1}
            inHoveredGroup={isHovered}
            isGroupStart={isGroupStart}
            followsHoveredGroup={isGroupStart && followsHoveredGroup}
            onSelect={onSelectStep}
            onOpen={onOpenStep}
            onToggleStepInChat={onToggleStepInChat}
            isReferencedInChat={
              step.number !== undefined &&
              (isStepReferencedInChat?.(step.number) ?? false)
            }
            status={
              step.number !== undefined
                ? getStepStatus?.(step.number)
                : undefined
            }
            io={
              step.number !== undefined ? getStepIO?.(step.number) : undefined
            }
          />
        );
      })}
    </Flex>
  );
});

/** Owns the hover that lifts a phase group. It sits below the title bar and
 * the IO tables so that tracking the pointer re-renders the step list alone,
 * and the memoised groups keep that down to the two whose spine changes. */
function DraftStepsList({
  phaseGroups,
  stickyTopClassName,
  ...stepProps
}: IDraftStepListProps & {
  phaseGroups: IStepPhaseGroup[];
  stickyTopClassName: string;
}) {
  const { t: tAutomation } = useTranslation('automation');
  const [hoveredGroup, setHoveredGroup] = useState<string>();

  /* A lift reads as "this is the phase", so it needs a named phase holding
   * more than the row already under the pointer. */
  const isLiftable = (group: IStepPhaseGroup) =>
    phaseGroups.length > 1 && group.phase !== '' && group.steps.length > 1;

  const selectedPhase = phaseGroups.find(group =>
    group.steps.some(step => step.number === stepProps.selectedStepNumber),
  )?.phase;
  const hoveredPhase = phaseGroups.find(
    group => groupKey(group) === hoveredGroup,
  )?.phase;
  const headingPhase = hoveredPhase || selectedPhase;

  return (
    <Flex direction="column" gap={2} width="full">
      <Flex
        align="center"
        gap={2}
        /* Spec 1.4: below the title bar, and fading out at its
           bottom edge so highlighted rows scroll cleanly beneath. */
        className={cn(
          'sticky z-20 -mx-10 w-[calc(100%+5rem)] bg-gradient-to-b from-card from-70% to-transparent px-10 py-4',
          stickyTopClassName,
        )}
        data-testid="draft-document-steps-heading"
      >
        <Text
          level="xSmall"
          color="muted"
          weight="semibold"
          className="uppercase tracking-[0.09em]"
        >
          {tAutomation('automation.manifestSopView.steps')}
        </Text>
        {headingPhase && (
          <Text
            level="xSmall"
            weight="semibold"
            className="uppercase tracking-[0.09em]"
            data-testid="draft-document-steps-heading-phase"
          >
            {headingPhase}
          </Text>
        )}
      </Flex>
      <Flex direction="column" width="full">
        {phaseGroups.map((group, groupIndex) => {
          const groupId = groupKey(group);
          const liftable = isLiftable(group);
          const previousGroup = phaseGroups[groupIndex - 1];
          return (
            <DraftDocumentPhaseGroup
              key={groupId}
              {...stepProps}
              group={group}
              groupId={groupId}
              /* Every group tracks the pointer, so a group that stops
               * lifting still clears the state on the way out. */
              isHovered={liftable && hoveredGroup === groupId}
              followsHoveredGroup={
                previousGroup !== undefined &&
                isLiftable(previousGroup) &&
                hoveredGroup === groupKey(previousGroup)
              }
              isFirstGroup={groupIndex === 0}
              isLastGroup={groupIndex === phaseGroups.length - 1}
              onHoverChange={setHoveredGroup}
            />
          );
        })}
      </Flex>
    </Flex>
  );
}

/** Draft-only document renderer. Published automation views continue using
 * `ManifestSopView`, which preserves the existing accordion experience. */
export function DraftDocumentStepsView({
  manifest,
  titleFallback,
  automationId,
  runId,
  updatedAt,
  showTitle = true,
  framed = true,
  padTop = !framed,
  isTitleEditable = false,
  stage = 'draft',
  version,
  onOpenVersionHistory,
  running,
  status,
  actions,
  onExport,
  exportManifest,
  onOpenWorkbench,
  onToggleMapView,
  selectedStepNumber,
  onSelectStep,
  onOpenStep,
  onToggleStepInChat,
  isStepReferencedInChat,
  getStepStatus,
  isRunActive = false,
  getStepIO,
  getInputValue,
  getOutputValue,
  inputValues,
  onInputValuesChange,
  defaultInputKeys,
  onToggleDefault,
  inputsReadOnly,
  stepDetailsUnavailable = false,
  stepDetailsFailed = false,
}: IDraftDocumentStepsViewProps) {
  const { t: tAutomation } = useTranslation('automation');
  const steps = manifest.steps ?? [];
  const viewportRef = useRef<HTMLDivElement>(null);
  const isFollowingRun = !!getStepStatus;
  const { getShownStatus, currentStepNumber } = useRunStepPlayback({
    stepNumbers: steps.flatMap(step =>
      step.number == null ? [] : [step.number],
    ),
    getStepStatus,
    isRunActive,
    runId,
  });
  const getStatus = isFollowingRun ? getShownStatus : undefined;
  const hasRunStarted = steps.some(
    step =>
      step.number != null &&
      (getStatus?.(step.number) ?? 'pending') !== 'pending',
  );
  const openStepNumber =
    currentStepNumber ??
    (isFollowingRun && !hasRunStarted ? steps[0]?.number : undefined);

  // eslint-disable-next-line no-restricted-syntax -- reason: centers the active step through the scroll viewport's imperative API
  useEffect(() => {
    if (currentStepNumber == null) return;
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const centerCurrentStep = () => {
      const viewport = viewportRef.current;
      const target = viewport?.querySelector(
        `[data-testid="draft-document-step-${currentStepNumber}"]`,
      );
      if (!viewport || !target) return;
      const bounds = target.getBoundingClientRect();
      const top =
        viewport.scrollTop +
        bounds.top -
        viewport.getBoundingClientRect().top +
        Math.min(bounds.height, viewport.clientHeight) / 2 -
        viewport.clientHeight / 2;
      viewport.scrollTo({
        top,
        behavior: reducedMotion ? 'instant' : 'smooth',
      });
    };
    // Scroll once the previous step has closed, so the target has stopped moving.
    const timer = window.setTimeout(
      centerCurrentStep,
      reducedMotion ? 0 : STEP_BODY_ANIM_MS + 20,
    );
    return () => window.clearTimeout(timer);
  }, [currentStepNumber, runId]);
  const phaseGroups = useMemo(
    () => groupStepsByPhase(manifest.steps ?? []),
    [manifest.steps],
  );
  const inputs = manifest.inputs ?? [];
  const outputs = manifest.outputs ?? [];
  // Section headers stick under the title bar, so they have to follow it when
  // the caller renders its own header instead (version history, diff): parked
  // at its height with nothing there, the document scrolls visibly through the
  // strip above them.
  const stickyTopClassName = showTitle ? 'top-14' : 'top-0';

  return (
    /* Spec 3: the card is the frame and its bottom edge stays pinned to the
     * viewport — the scroll lives *inside* it. Scrolling the card itself would
     * grow it past the fold and carry its bottom border off-screen. */
    <Flex
      direction="column"
      /* Whole-card and title-driven on purpose: the section headings (Inputs,
       * Steps, Outputs) have to sit on the same edge as the content they label. */
      dir="auto"
      className={cn(
        'h-full w-full shrink-0 overflow-hidden',
        framed && 'rounded-2xl border border-border bg-card shadow-xs',
      )}
      data-testid="draft-document-steps-view"
    >
      <Flex
        direction="column"
        className="min-h-0 w-full flex-1 overflow-auto px-10 pb-10 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="draft-document-scroll"
        ref={viewportRef}
      >
        <Flex
          direction="column"
          gap={6}
          width="full"
          // Without its title bar nothing sits above the first section. The
          // space is on the content, not the scroller, so it scrolls away and
          // the sticky section headers still pin to the top edge.
          className={padTop ? 'pt-8' : undefined}
        >
          {/* Spec 1: the title bar is the scroll container's first child, so it
              stays pinned to the card's top edge while the document scrolls
              under it. */}
          {showTitle && (
            <AutomationDocumentTitleBar
              title={titleFallback || manifest.title}
              automationId={automationId}
              stage={stage}
              lastSavedAt={updatedAt}
              isTitleEditable={isTitleEditable}
              onOpenWorkbench={onOpenWorkbench}
              onToggleMapView={onToggleMapView}
              version={version}
              onOpenVersionHistory={onOpenVersionHistory}
              running={running}
              status={status}
              actions={actions}
              onExport={onExport}
              exportManifest={exportManifest}
              exportTitleFallback={titleFallback}
            />
          )}
          {manifest.overview && (
            <Flex
              align="start"
              gap={2}
              className="mx-auto w-full max-w-[720px]"
            >
              <Flex
                direction="column"
                align="stretch"
                className="min-w-0 flex-1"
              >
                <ManifestClampedText
                  text={manifest.overview}
                  textProps={OVERVIEW_TEXT_PROPS}
                />
              </Flex>
            </Flex>
          )}

          {stepDetailsUnavailable && (
            <Flex
              gap={2}
              align="center"
              className="mx-auto w-full max-w-[720px] rounded-md border p-3"
              data-testid="manifest-step-details-unavailable"
            >
              <Icon type="CircleAlert" size="sm" className="shrink-0" />
              <Flex direction="column" className="min-w-0 flex-1">
                <Text level="small" weight="medium">
                  {tAutomation(
                    'automation.manifestSopView.runEvents.errorTitle',
                  )}
                </Text>
                <Text level="xSmall" color="muted">
                  {tAutomation(
                    stepDetailsFailed
                      ? 'automation.manifestSopView.runEvents.errorDescription'
                      : 'automation.manifestSopView.runEvents.emptyDescription',
                  )}
                </Text>
              </Flex>
            </Flex>
          )}

          <Flex
            direction="column"
            gap={6}
            className="mx-auto w-full max-w-[720px]"
          >
            <ManifestIOTable
              title={tAutomation('automation.manifestSopView.inputs')}
              items={inputs}
              getValue={getInputValue}
              inputValues={inputValues}
              onInputValuesChange={onInputValuesChange}
              defaultInputKeys={defaultInputKeys}
              onToggleDefault={onToggleDefault}
              inputsReadOnly={inputsReadOnly}
              showValueColumn
              variant="document"
              hasTitleBar={showTitle}
            />

            {phaseGroups.length > 0 ? (
              <DraftStepsList
                phaseGroups={phaseGroups}
                stickyTopClassName={stickyTopClassName}
                runId={runId}
                selectedStepNumber={selectedStepNumber}
                onSelectStep={onSelectStep}
                onOpenStep={onOpenStep}
                onToggleStepInChat={onToggleStepInChat}
                isStepReferencedInChat={isStepReferencedInChat}
                getStepStatus={getStatus}
                getStepIO={getStepIO}
                isFollowingRun={isFollowingRun}
                openStepNumber={openStepNumber}
                currentStepNumber={currentStepNumber}
              />
            ) : (
              <EmptyState
                icon="Workflow"
                title={tAutomation(
                  'automation.manifestSopView.emptySteps.title',
                )}
                description={tAutomation(
                  'automation.manifestSopView.emptySteps.description',
                )}
              />
            )}

            <ManifestIOTable
              title={tAutomation('automation.manifestSopView.outputs')}
              items={outputs}
              getValue={getOutputValue}
              variant="document"
              hasTitleBar={showTitle}
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
